"use client";

import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { PublicBookingLink } from "@/components/dashboard/settings/public-booking-link";
import { NotifReminderToggles } from "@/components/dashboard/notif-reminder-toggles";
import { SettingsPublicProfileBlock } from "@/components/dashboard/settings-public-profile-block";
import { SettingsLocationBlock, type LocationValue } from "@/components/dashboard/settings-location-block";
import { SettingsAmenitiesBlock } from "@/components/dashboard/settings-amenities-block";
import type { AmenityItem } from "@/lib/amenities-catalog";
import { parsePublicProfileThemeId, type PublicProfileThemeId } from "@/lib/public-profile-theme-vars";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useToast } from "@/hooks/useToast";

type SettingsJson = { slug: string; timezone: string; publicBookingPath: string };

type MeSlice = {
  name: string;
  phone: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  publicBookingEnabled: boolean;
  modality: string;
  information: string | null;
  confirmationMessage: string | null;
  plan: string;
  bio: string | null;
  profileTheme: PublicProfileThemeId;
  primaryColor: string | null;
  notify24h: boolean;
  notify1h: boolean;
  acceptsCash: boolean;
  acceptsTransfer: boolean;
  acceptsOther: boolean;
  specialty: string | null;
  address: string | null;
  addressLat: number | null;
  addressLng: number | null;
  addressPlaceId: string | null;
  amenities: AmenityItem[];
};

function parseAmenities(raw: unknown): AmenityItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is AmenityItem =>
      a != null &&
      typeof a === "object" &&
      typeof (a as AmenityItem).id === "string" &&
      typeof (a as AmenityItem).nombre === "string" &&
      typeof (a as AmenityItem).icono === "string" &&
      ((a as AmenityItem).tipo === "predefinido" || (a as AmenityItem).tipo === "custom"),
  );
}

const INFO_MAX_TEXT = 1500;

function plainTextFromHtml(html: string): string {
  if (typeof document === "undefined") return html.replace(/<[^>]+>/g, " ");
  const d = document.createElement("div");
  d.innerHTML = html;
  return (d.textContent || d.innerText || "").replace(/\s+/g, " ").trim();
}

function PlanOrderIndex(plan: string): number {
  const o = ["FREE", "PRO", "ENTERPRISE"] as const;
  const i = o.indexOf(plan as (typeof o)[number]);
  return i >= 0 ? i : 0;
}

function canUseCombinada(plan: string) {
  return PlanOrderIndex(plan) >= PlanOrderIndex("PRO");
}

function parseProfileTheme(raw: unknown): PublicProfileThemeId {
  return parsePublicProfileThemeId(typeof raw === "string" ? raw : undefined);
}

export function DashboardSettings() {
  const { refresh } = useAuth();
  const toast = useToast();
  const editorRef = useRef<HTMLDivElement>(null);

  const [s, setS] = useState<SettingsJson | null>(null);
  const [origin, setOrigin] = useState("");
  const [plan, setPlan] = useState("FREE");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [publicBookingEnabled, setPublicBookingEnabled] = useState(true);
  const [patchingPublicBooking, setPatchingPublicBooking] = useState(false);
  const [modality, setModality] = useState("presencial");
  const [informationHtml, setInformationHtml] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [bio, setBio] = useState("");
  const [profileTheme, setProfileTheme] = useState<PublicProfileThemeId>("classic");
  const [primaryColor, setPrimaryColor] = useState("");
  const [reminderPrefs, setReminderPrefs] = useState({ notify24h: true, notify1h: false });
  const [acceptsCash, setAcceptsCash] = useState(true);
  const [acceptsTransfer, setAcceptsTransfer] = useState(true);
  const [acceptsOther, setAcceptsOther] = useState(true);
  const [specialty, setSpecialty] = useState("");
  const [location, setLocation] = useState<LocationValue>({ address: "", lat: null, lng: null, placeId: null });
  const [amenities, setAmenities] = useState<AmenityItem[]>([]);

  const [saved, setSaved] = useState<MeSlice | null>(null);
  const [saving, setSaving] = useState(false);
  const [patchingActive, setPatchingActive] = useState(false);

  const [accountEmail, setAccountEmail] = useState("");
  const [newEmailDraft, setNewEmailDraft] = useState("");
  const [emailChangePassword, setEmailChangePassword] = useState("");
  const [emailChangeBusy, setEmailChangeBusy] = useState(false);

  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarFileRef = useRef<HTMLInputElement>(null);

  const applyMe = useCallback((m: MeSlice) => {
    setName(m.name);
    setPhone(m.phone ?? "");
    setAvatarUrl(m.avatarUrl ?? "");
    setIsActive(m.isActive);
    setPublicBookingEnabled(m.publicBookingEnabled);
    setModality(m.modality || "presencial");
    setInformationHtml(m.information ?? "");
    setConfirmationMessage(m.confirmationMessage ?? "");
    setBio(m.bio ?? "");
    setProfileTheme(m.profileTheme);
    setPrimaryColor(m.primaryColor ?? "");
    setReminderPrefs({ notify24h: m.notify24h, notify1h: m.notify1h });
    setAcceptsCash(m.acceptsCash !== false);
    setAcceptsTransfer(m.acceptsTransfer !== false);
    setAcceptsOther(m.acceptsOther !== false);
    setSpecialty(m.specialty ?? "");
    setLocation({ address: m.address ?? "", lat: m.addressLat, lng: m.addressLng, placeId: m.addressPlaceId });
    setAmenities(m.amenities);
    setPlan(m.plan || "FREE");
    setSaved(m);
    if (editorRef.current) editorRef.current.innerHTML = m.information ?? "";
  }, []);

  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [rSet, rMe] = await Promise.all([
        fetchWithSessionSync("/api/dashboard/settings", { credentials: "include" }, refresh),
        fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh),
      ]);
      if (cancelled) return;
      if (rSet.ok) setS((await rSet.json()) as SettingsJson);
      if (rMe.ok) {
        const d = (await rMe.json()) as { professional?: Record<string, unknown>; email?: string };
        if (typeof d.email === "string") setAccountEmail(d.email);
        const p = d.professional;
        if (p) {
          applyMe({
            name: String(p.name ?? ""),
            phone: (p.phone as string | null) ?? null,
            avatarUrl: (p.avatarUrl as string | null) ?? null,
            isActive: Boolean(p.isActive ?? true),
            publicBookingEnabled: Boolean(p.publicBookingEnabled ?? true),
            modality: String(p.modality ?? "presencial"),
            information: (p.information as string | null) ?? null,
            confirmationMessage: (p.confirmationMessage as string | null) ?? null,
            plan: String(p.plan ?? "FREE"),
            bio: (p.bio as string | null) ?? null,
            profileTheme: parseProfileTheme(p.profileTheme),
            primaryColor: (p.primaryColor as string | null) ?? null,
            notify24h: p.notify24h !== false,
            notify1h: Boolean(p.notify1h),
            acceptsCash: p.acceptsCash !== false,
            acceptsTransfer: p.acceptsTransfer !== false,
            acceptsOther: p.acceptsOther !== false,
            specialty: (p.specialty as string | null) ?? null,
            address: (p.address as string | null) ?? null,
            addressLat: (p.addressLat as number | null) ?? null,
            addressLng: (p.addressLng as number | null) ?? null,
            addressPlaceId: (p.addressPlaceId as string | null) ?? null,
            amenities: parseAmenities(p.amenities),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh, applyMe]);

  const serverInformation = saved?.information ?? null;
  const profileReady = saved != null;
  useEffect(() => {
    const el = editorRef.current;
    if (!el || !profileReady) return;
    el.innerHTML = serverInformation ?? "";
  }, [serverInformation, profileReady]);

  const dirty = useMemo(() => {
    if (!saved) return false;
    return (
      name.trim() !== saved.name.trim() ||
      phone.trim() !== (saved.phone ?? "").trim() ||
      avatarUrl.trim() !== (saved.avatarUrl ?? "").trim() ||
      modality !== saved.modality ||
      informationHtml !== (saved.information ?? "") ||
      confirmationMessage.trim() !== (saved.confirmationMessage ?? "").trim() ||
      bio.trim() !== (saved.bio ?? "").trim() ||
      profileTheme !== saved.profileTheme ||
      primaryColor.trim() !== (saved.primaryColor ?? "").trim() ||
      acceptsCash !== saved.acceptsCash ||
      acceptsTransfer !== saved.acceptsTransfer ||
      acceptsOther !== saved.acceptsOther ||
      specialty.trim() !== (saved.specialty ?? "").trim() ||
      location.address.trim() !== (saved.address ?? "").trim() ||
      location.lat !== saved.addressLat ||
      location.lng !== saved.addressLng ||
      location.placeId !== saved.addressPlaceId ||
      JSON.stringify(amenities) !== JSON.stringify(saved.amenities)
    );
  }, [
    saved,
    name,
    phone,
    avatarUrl,
    modality,
    informationHtml,
    confirmationMessage,
    bio,
    profileTheme,
    primaryColor,
    acceptsCash,
    acceptsTransfer,
    acceptsOther,
    specialty,
    location,
    amenities,
  ]);

  async function uploadAvatarFile(file: File) {
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      toast.error("Solo JPG, PNG, WebP o GIF.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen no puede superar 2 MB.");
      return;
    }
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const rUp = await fetchWithSessionSync("/api/dashboard/upload/avatar", { method: "POST", body: fd }, refresh);
      const up = (await rUp.json().catch(() => ({}))) as { error?: string; url?: string };
      if (!rUp.ok) throw new Error(up.error ?? "No se pudo subir la imagen");
      const url = up.url;
      if (!url) throw new Error("Respuesta inválida del servidor");

      const rPatch = await fetchWithSessionSync(
        "/api/dashboard/me",
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ avatarUrl: url }),
        },
        refresh,
      );
      const patchBody = (await rPatch.json().catch(() => ({}))) as { error?: string; professional?: { avatarUrl?: string | null } };
      if (!rPatch.ok) throw new Error(patchBody.error ?? "Subida OK pero no se pudo guardar el perfil");
      const savedUrl = patchBody.professional?.avatarUrl ?? url;
      setAvatarUrl(savedUrl);
      setSaved((prev) => (prev ? { ...prev, avatarUrl: savedUrl } : prev));
      toast.success("Foto de perfil actualizada.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al subir la foto");
    } finally {
      setAvatarUploading(false);
      if (avatarFileRef.current) avatarFileRef.current.value = "";
    }
  }

  async function saveGeneral() {
    if (!saved) return;
    const textLen = plainTextFromHtml(informationHtml);
    if (textLen.length > INFO_MAX_TEXT) {
      toast.error(`La información supera ${INFO_MAX_TEXT} caracteres.`);
      return;
    }
    if (modality === "combinada" && !canUseCombinada(plan)) {
      toast.error("La modalidad combinada requiere plan Pro o superior.");
      return;
    }
    setSaving(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim() || null,
          avatarUrl: avatarUrl.trim() || null,
          modality,
          information: informationHtml.trim() || null,
          confirmationMessage: confirmationMessage.trim() || null,
          bio: bio.trim() || null,
          profileTheme,
          primaryColor: primaryColor.trim() || null,
          acceptsCash,
          acceptsTransfer,
          acceptsOther,
          specialty: specialty.trim() || null,
          address: location.address.trim() || null,
          addressLat: location.lat,
          addressLng: location.lng,
          addressPlaceId: location.placeId,
          amenities,
        }),
      },
      refresh,
    );
    setSaving(false);
    if (!r.ok) {
      toast.error("No se pudo guardar.");
      return;
    }
    const d = (await r.json()) as { professional?: Record<string, unknown> };
    const p = d.professional;
    if (p) {
      applyMe({
        name: String(p.name ?? ""),
        phone: (p.phone as string | null) ?? null,
        avatarUrl: (p.avatarUrl as string | null) ?? null,
        isActive: Boolean(p.isActive ?? true),
        publicBookingEnabled: Boolean(p.publicBookingEnabled ?? true),
        modality: String(p.modality ?? "presencial"),
        information: (p.information as string | null) ?? null,
        confirmationMessage: (p.confirmationMessage as string | null) ?? null,
        plan: String(p.plan ?? "FREE"),
        bio: (p.bio as string | null) ?? null,
        profileTheme: parseProfileTheme(p.profileTheme),
        primaryColor: (p.primaryColor as string | null) ?? null,
        notify24h: p.notify24h !== false,
        notify1h: Boolean(p.notify1h),
        acceptsCash: p.acceptsCash !== false,
        acceptsTransfer: p.acceptsTransfer !== false,
        acceptsOther: p.acceptsOther !== false,
        specialty: (p.specialty as string | null) ?? null,
        address: (p.address as string | null) ?? null,
        addressLat: (p.addressLat as number | null) ?? null,
        addressLng: (p.addressLng as number | null) ?? null,
        addressPlaceId: (p.addressPlaceId as string | null) ?? null,
        amenities: parseAmenities(p.amenities),
      });
    }
    toast.success("Guardado");
  }

  async function toggleActive(next: boolean) {
    if (patchingActive) return;
    setPatchingActive(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: next }),
      },
      refresh,
    );
    setPatchingActive(false);
    if (!r.ok) {
      toast.error("No se pudo actualizar el estado.");
      return;
    }
    setIsActive(next);
    if (saved) setSaved({ ...saved, isActive: next });
    toast.success(next ? "Perfil visible para reservas" : "Perfil oculto");
  }

  async function togglePublicBooking(next: boolean) {
    if (patchingPublicBooking) return;
    setPatchingPublicBooking(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ publicBookingEnabled: next }),
      },
      refresh,
    );
    setPatchingPublicBooking(false);
    if (!r.ok) {
      toast.error("No se pudo actualizar reservas online.");
      return;
    }
    setPublicBookingEnabled(next);
    if (saved) setSaved({ ...saved, publicBookingEnabled: next });
    toast.success(next ? "Reservas online activadas" : "Reservas online desactivadas");
  }

  function exec(cmd: "bold" | "italic" | "insertUnorderedList") {
    editorRef.current?.focus();
    try {
      document.execCommand(cmd, false);
    } catch {
      /* noop */
    }
    setInformationHtml(editorRef.current?.innerHTML ?? "");
  }

  function onInfoInput() {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    const len = plainTextFromHtml(html).length;
    if (len > INFO_MAX_TEXT) {
      toast.error(`Máximo ${INFO_MAX_TEXT} caracteres de texto.`);
      el.innerHTML = informationHtml;
      return;
    }
    setInformationHtml(html);
  }

  if (!s || !saved) {
    return (
      <DashboardPageShell eyebrow="Tu cuenta" title="Configuración">
        <LoadingFrame variant="light" minHeight={260} />
      </DashboardPageShell>
    );
  }

  const publicUrl = `${origin}${s.publicBookingPath}`;
  const infoTextLen = plainTextFromHtml(informationHtml).length;

  return (
    <DashboardPageShell
      eyebrow="Tu cuenta"
      title="Configuración"
      description="Datos de tu perfil público e información para clientes."
    >
      <div className={cx(cf.dashSurface, css({ p: { base: "4", md: "6" }, mb: "6" }))}>
        <h2 className={css({ fontSize: "lg", fontWeight: "semibold", color: "#0f172a", mb: "2" })}>Correo de acceso</h2>
        <p className={css({ fontSize: "sm", color: "#64748b", mb: "4", lineHeight: "1.5" })}>
          Iniciás sesión con <strong>{accountEmail || "…"}</strong>. Para usar otro correo, pedimos confirmación en el nuevo email.
        </p>
        <div className={css({ display: "grid", gap: "3", maxW: "28rem" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
            Nuevo correo
            <input
              type="email"
              autoComplete="email"
              value={newEmailDraft}
              onChange={(e) => setNewEmailDraft(e.target.value)}
              className={css({
                px: "3",
                py: "2",
                rounded: "md",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                fontSize: "sm",
              })}
            />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
            Contraseña actual
            <input
              type="password"
              autoComplete="current-password"
              value={emailChangePassword}
              onChange={(e) => setEmailChangePassword(e.target.value)}
              className={css({
                px: "3",
                py: "2",
                rounded: "md",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                fontSize: "sm",
              })}
            />
          </label>
          <button
            type="button"
            disabled={emailChangeBusy || !newEmailDraft.trim() || !emailChangePassword}
            onClick={() => {
              void (async () => {
                setEmailChangeBusy(true);
                const r = await fetchWithSessionSync(
                  "/api/dashboard/account/request-email-change",
                  {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      newEmail: newEmailDraft.trim().toLowerCase(),
                      password: emailChangePassword,
                    }),
                  },
                  refresh,
                );
                setEmailChangeBusy(false);
                const j = (await r.json().catch(() => ({}))) as { error?: string };
                if (!r.ok) {
                  toast.error(j.error ?? "No se pudo enviar la solicitud.");
                  return;
                }
                toast.success("Revisá el correo nuevo: tenés un enlace para confirmar.");
                setEmailChangePassword("");
              })();
            }}
            className={css({
              px: "4",
              py: "2",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              color: "white",
              bg: "#4f46e5",
              border: "none",
              cursor: emailChangeBusy ? "wait" : "pointer",
              alignSelf: "flex-start",
              _disabled: { opacity: 0.5, cursor: "not-allowed" },
            })}
          >
            {emailChangeBusy ? "Enviando…" : "Enviar confirmación al nuevo correo"}
          </button>
        </div>
      </div>

      <div
        className={css({
          position: "sticky",
          top: { base: "0", md: "0" },
          zIndex: 3,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "3",
          py: "3",
          px: "4",
          mb: "6",
          rounded: "xl",
          bg: "rgba(248,250,252,0.95)",
          backdropFilter: "blur(8px)",
          borderWidth: "1px",
          borderColor: "#e2e8f0",
        })}
      >
        <div className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" })}>
          {dirty ? (
            <span
              className={css({
                fontSize: "xs",
                fontWeight: "semibold",
                px: "2",
                py: "1",
                rounded: "md",
                bg: "#fef3c7",
                color: "#92400e",
              })}
            >
              Cambios sin guardar
            </span>
          ) : (
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Al día</span>
          )}
        </div>
        <button
          type="button"
          disabled={saving || !dirty}
          onClick={() => void saveGeneral()}
          className={css({
            px: "4",
            py: "2",
            rounded: "lg",
            fontSize: "sm",
            fontWeight: "semibold",
            color: "white",
            border: "none",
            cursor: saving || !dirty ? "not-allowed" : "pointer",
            opacity: saving || !dirty ? 0.5 : 1,
            bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
          })}
        >
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>

      <div className={css({ display: "flex", flexDir: "column", gap: "6", maxW: "42rem" })}>
        <PublicBookingLink href={publicUrl} label="Tu página de reservas (compartila con clientes)" />

        <div className={cx(cf.dashSurface, css({ p: "5" }))}>
          <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>Identificador público (slug)</p>
          <p className={css({ fontWeight: "semibold", color: "#0f172a", fontFamily: "mono", fontSize: "sm" })}>{s.slug}</p>
        </div>

        <SettingsPublicProfileBlock
          slug={s.slug}
          origin={origin}
          bio={bio}
          onBioChange={setBio}
          profileTheme={profileTheme}
          onProfileThemeChange={setProfileTheme}
          primaryColor={primaryColor}
          onPrimaryColorChange={setPrimaryColor}
        />

        <div>
          <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "3", fontSize: "sm" })}>
            Recordatorios a tus clientes
          </p>
          <NotifReminderToggles
            initial={reminderPrefs}
            plan={plan}
            onPrefsChange={(p) => {
              setReminderPrefs(p);
              setSaved((prev) => (prev ? { ...prev, notify24h: p.notify24h, notify1h: p.notify1h } : prev));
            }}
          />
        </div>

        <div className={cx(cf.dashSurface, css({ p: "5" }))}>
          <label className={css({ display: "flex", alignItems: "center", gap: "3", fontSize: "sm", color: "#334155", mb: "4" })}>
            <input
              type="checkbox"
              checked={isActive}
              disabled={patchingActive}
              onChange={(e) => void toggleActive(e.target.checked)}
            />
            Perfil activo (acepta reservas públicas)
          </label>

          <div className={css({ mb: "4", p: "3.5", rounded: "lg", bg: "#f8fafc", borderWidth: "1px", borderColor: "#e2e8f0" })}>
            <label className={css({ display: "flex", alignItems: "center", gap: "3", fontSize: "sm", color: "#334155", cursor: patchingPublicBooking ? "wait" : "pointer" })}>
              <input
                type="checkbox"
                checked={publicBookingEnabled}
                disabled={patchingPublicBooking}
                onChange={(e) => void togglePublicBooking(e.target.checked)}
              />
              <span className={css({ fontWeight: "medium" })}>Reservas online</span>
            </label>
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "1.5", ml: "6.5", lineHeight: "1.5" })}>
              Permitir que los pacientes reserven desde el link público. Cuando está desactivado, el link de tu
              agenda muestra un mensaje de contacto en lugar del formulario de reserva.
            </p>
          </div>

          <label className={css({ display: "flex", flexDir: "column", gap: "2", fontSize: "sm", color: "#334155", mb: "4" })}>
            <span>Nombre del negocio o profesional</span>
            <input className={cf.input} value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className={css({ display: "flex", flexDir: "column", gap: "2", fontSize: "sm", color: "#334155", mb: "4" })}>
            <span>Especialidad</span>
            <input
              className={cf.input}
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              placeholder="Ej: Kinesiología, Odontología, Psicología…"
              maxLength={120}
            />
            <span className={css({ fontSize: "xs", color: "#64748b" })}>
              Opcional. Se muestra en tu perfil público para identificar tu área de trabajo.
            </span>
          </label>

          <label className={css({ display: "flex", flexDir: "column", gap: "2", fontSize: "sm", color: "#334155", mb: "4" })}>
            <span>Teléfono de contacto</span>
            <input className={cf.input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+54 …" />
          </label>

          <div className={css({ mb: "4" })}>
            <p className={css({ fontSize: "sm", color: "#334155", mb: "2", fontWeight: "medium" })}>Foto de perfil</p>
            <p className={css({ fontSize: "xs", color: "#64748b", mb: "3", lineHeight: "1.5" })}>
              Subí una imagen desde tu dispositivo. Se muestra en <span className={css({ fontFamily: "mono" })}>/book/…</span> y en el perfil público; se guarda en Cloudinary{" "}
              <span className={css({ fontFamily: "mono" })}>sfturnos/{s?.slug ?? "tu-slug"}/</span> (JPG, PNG, WebP o GIF, máx. 2 MB).
            </p>
            <div className={css({ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4" })}>
              {avatarUrl.trim() ? (
                <img
                  src={avatarUrl.trim()}
                  alt=""
                  width={80}
                  height={80}
                  className={css({ rounded: "full", objectFit: "cover", borderWidth: "1px", borderColor: "#e2e8f0" })}
                />
              ) : (
                <div
                  className={css({
                    w: "20",
                    h: "20",
                    rounded: "full",
                    bg: "#f1f5f9",
                    borderWidth: "1px",
                    borderColor: "#e2e8f0",
                    flexShrink: 0,
                  })}
                />
              )}
              <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
                <input
                  ref={avatarFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className={css({ display: "none" })}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void uploadAvatarFile(f);
                  }}
                />
                <button
                  type="button"
                  disabled={avatarUploading}
                  onClick={() => avatarFileRef.current?.click()}
                  className={css({
                    fontSize: "sm",
                    px: "4",
                    py: "2",
                    rounded: "md",
                    borderWidth: "1px",
                    borderColor: "#cbd5e1",
                    bg: "#fff",
                    cursor: avatarUploading ? "not-allowed" : "pointer",
                    opacity: avatarUploading ? 0.7 : 1,
                    alignSelf: "flex-start",
                  })}
                >
                  {avatarUploading ? "Subiendo…" : "Elegir imagen"}
                </button>
              </div>
            </div>
          </div>

          <div className={css({ mb: "4" })}>
            <p className={css({ fontSize: "sm", color: "#334155", mb: "2" })}>Modalidad</p>
            <select
              className={cf.input}
              value={modality}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "combinada" && !canUseCombinada(plan)) {
                  toast.error("La modalidad combinada es un beneficio Pro.");
                  return;
                }
                setModality(v);
              }}
            >
              <option value="presencial">Presencial</option>
              <option value="virtual">Virtual</option>
              <option value="combinada">Combinada (Pro)</option>
            </select>
          </div>

          <div className={css({ mt: "4", pt: "4", borderTopWidth: "1px", borderColor: "#e2e8f0" })}>
            <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "2", fontSize: "sm" })}>Medios de cobro (perfil público)</p>
            <p className={css({ fontSize: "xs", color: "#64748b", mb: "3", lineHeight: "1.5" })}>
              Mostramos en tu página <span className={css({ fontFamily: "mono" })}>/p/…</span> qué opciones aceptás además de Mercado Pago online.
            </p>
            <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm", color: "#334155", mb: "2" })}>
              <input type="checkbox" checked={acceptsCash} onChange={(e) => setAcceptsCash(e.target.checked)} />
              Efectivo
            </label>
            <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm", color: "#334155", mb: "2" })}>
              <input type="checkbox" checked={acceptsTransfer} onChange={(e) => setAcceptsTransfer(e.target.checked)} />
              Transferencia
            </label>
            <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm", color: "#334155" })}>
              <input type="checkbox" checked={acceptsOther} onChange={(e) => setAcceptsOther(e.target.checked)} />
              Otros acuerdos / medios
            </label>
          </div>
        </div>

        <SettingsLocationBlock value={location} onChange={setLocation} />

        <SettingsAmenitiesBlock value={amenities} onChange={setAmenities} />

        <div className={cx(cf.dashSurface, css({ p: "5" }))}>
          <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "2" })}>Información en el flujo de reservas</p>
          <p className={css({ fontSize: "xs", color: "#64748b", mb: "3", lineHeight: "1.5" })}>
            Texto enriquecido (HTML) que ven los clientes en el asistente de reservas <span className={css({ fontFamily: "mono" })}>/book/…</span>. No se
            usa en el perfil público <span className={css({ fontFamily: "mono" })}>/p/…</span> (allí va la biografía en markdown).
          </p>
          <div className={css({ display: "flex", gap: "2", mb: "2", flexWrap: "wrap" })}>
            <button
              type="button"
              onClick={() => exec("bold")}
              className={css({
                fontSize: "xs",
                px: "3",
                py: "1.5",
                rounded: "md",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                bg: "#f8fafc",
                cursor: "pointer",
              })}
            >
              Negrita
            </button>
            <button
              type="button"
              onClick={() => exec("italic")}
              className={css({
                fontSize: "xs",
                px: "3",
                py: "1.5",
                rounded: "md",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                bg: "#f8fafc",
                cursor: "pointer",
              })}
            >
              Cursiva
            </button>
            <button
              type="button"
              onClick={() => exec("insertUnorderedList")}
              className={css({
                fontSize: "xs",
                px: "3",
                py: "1.5",
                rounded: "md",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                bg: "#f8fafc",
                cursor: "pointer",
              })}
            >
              Lista
            </button>
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className={css({
              minH: "120px",
              p: "3",
              rounded: "lg",
              borderWidth: "1px",
              borderColor: "#e2e8f0",
              fontSize: "sm",
              lineHeight: "1.5",
              outline: "none",
              _focusVisible: { borderColor: "token(colors.brand.400)" },
            })}
            onInput={onInfoInput}
            onBlur={onInfoInput}
          />
          <p className={css({ fontSize: "xs", color: "#64748b", mt: "2" })}>
            {infoTextLen}/{INFO_MAX_TEXT} caracteres
          </p>
        </div>

        <div className={cx(cf.dashSurface, css({ p: "5" }))}>
          <label className={css({ display: "flex", flexDir: "column", gap: "2", fontSize: "sm", color: "#334155" })}>
            <span>Mensaje de confirmación para el cliente</span>
            <textarea
              className={cx(cf.input, css({ minH: "100px", resize: "vertical" }))}
              value={confirmationMessage}
              onChange={(e) => setConfirmationMessage(e.target.value)}
              placeholder="Ej. Gracias por reservar. Te esperamos…"
            />
          </label>
          <p className={css({ fontSize: "xs", color: "#64748b", mt: "2", lineHeight: "1.5" })}>
            Este mensaje aparecerá en el email de confirmación (próximamente).
          </p>
        </div>

        <div className={cx(cf.dashSurface, css({ p: "5" }))}>
          <p className={css({ fontSize: "xs", color: "#64748b", mb: "1" })}>Zona horaria</p>
          <p className={css({ fontSize: "sm", color: "#334155" })}>{s.timezone}</p>
          <p className={css({ fontSize: "xs", color: "#64748b", mt: "3", lineHeight: "1.5" })}>
            Los horarios de turnos y recordatorios respetan esta zona. Si necesitás cambiarla, contactá soporte o administración.
          </p>
        </div>

      </div>
    </DashboardPageShell>
  );
}
